
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn
} from '@angular/common/http';
import { DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  from,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { TokenService } from '../authentication/token.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';

// Singleton state for token refresh
const state = {
  refreshTokenInProgress: false,
  refreshTokenSubject: new BehaviorSubject<string | null>(null)
};

const WHITELIST = ['/assets/'] as const;

export const httpConfigInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const isWhitelisted = WHITELIST.some(path => request.url.includes(path));
  if (isWhitelisted) {
    return next(request);
  }

  const router = inject(Router);
  const authService = inject(AuthenticationService);
  const tokenService = inject(TokenService);
  const destroyRef = inject(DestroyRef);

  const skipAuthUrls = ['/login','/userLogin', '/refreshToken'];
  const skipAuth = skipAuthUrls.some(url => request.url.endsWith(url));
  if (!skipAuth) {
    let token = tokenService.getToken();
    if (!token) {
      authService.logout();
      router.navigateByUrl('/login');
    }
    request = addAuthHeader(request, token, request.url);
  }

  const url = isWhitelisted ? request.url : `${environment.APIURL}${request.url}`;
  request = addAuthHeader(request, null, url);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (request.url.endsWith('/refreshToken')) {
          // Refresh token has failed - logout user
          authService.logout();
          router.navigateByUrl('/login');
          return throwError(() => new Error('Refresh token failed'));
        }

      if (!state.refreshTokenInProgress) {
        state.refreshTokenInProgress = true;
        state.refreshTokenSubject.next(null);

        const refreshToken = tokenService.getRefreshToken();
        if (!refreshToken || new Date().getTime() > new Date(tokenService.getRefreshTokenExpiration()).getTime()) {
          authService.logout();
          router.navigateByUrl('/login');
          return throwError(() => new Error('Session expired'));
        }

        return from(authService.refreshToken()).pipe(
          takeUntilDestroyed(destroyRef),
          switchMap(token => {
            state.refreshTokenInProgress = false;
            if (token) {
              state.refreshTokenSubject.next(token);
              // Retry the failed request with new token
              return next(addAuthHeader(request, token, request.url));
            }
            // If no token received, force logout
            authService.logout();
            router.navigateByUrl('/login');
            return throwError(() => new Error('No token received'));
          }),
          catchError(err => {
            state.refreshTokenInProgress = false;
            authService.logout();
            router.navigateByUrl('/login');
            return throwError(() => new Error('Failed to refresh token'));
          })
        );
      } else {
        // Wait for the ongoing refresh token request
        return state.refreshTokenSubject.pipe(
          takeUntilDestroyed(destroyRef),
          filter((token): token is string => token !== null),
          take(1),
          switchMap(token => {
            return next(addAuthHeader(request, token, request.url));
          })
        );
      }
    }

    return throwError(() => error);
  })
  );
};


function addAuthHeader(
  request: HttpRequest<unknown>,
  token: string | null,
  url: string
): HttpRequest<unknown> {
  const lang = localStorage.getItem("lang") ?? "ar";
  return request.clone({
    url,
    setHeaders: {
      'Accept-Language': lang,
      ...(token && { 'Authorization': `Bearer ${token}` })
    }
  });
}
